import { DestroyRef, Injectable, inject } from '@angular/core';
import { AuthService } from "@app/core/services/auth.service";
import { TIMEOUT } from "@app/core/config/global.config";
import { fromEvent, merge, throttleTime } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Injectable({
  providedIn: 'root'
})
export class InactivityService {

  private timeoutId: any;
  private started = false;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private authService: AuthService
    ) {}

  public startInactivityWatch(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    const events = ['click', 'keydown', 'touchstart'];

    // High-frequency events (like mousemove) can cause a lot of work. Throttle activity updates.
    const activity$ = merge(
      ...events.map(eventName => fromEvent(window, eventName, { passive: true }))
    ).pipe(
      throttleTime(1000, undefined, { leading: true, trailing: true }),
      takeUntilDestroyed(this.destroyRef)
    );

    activity$.subscribe(() => {
      this.resetTimer();
      // Used for cross-tab sync; throttle prevents excessive storage writes.
      localStorage.setItem('lastActivity', Date.now().toString());
    });

    fromEvent<StorageEvent>(window, 'storage').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event) => {
      if (event.key === 'lastActivity') {
        this.resetTimer();
      }
    });

    fromEvent(document, 'visibilitychange').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (document.visibilityState === 'visible') {
        this.resetTimer();
      }
    });

    this.resetTimer();
  }

  private resetTimer(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      this.logout();
    }, TIMEOUT);
  }

  private logout(): void {
    // If user is already unauthenticated (e.g. currently on login page),
    // don't trigger another logout that could overwrite redirectUrl.
    if (!this.authService.isAuthenticated()) {
      return;
    }
    this.authService.onLogout();
  }
}
